/**
 * Waritto プロフィールフック
 * 共通フックを使用し、支払い情報（PayPay URL）を追加
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAtom } from "jotai";
import { ApiError } from "@tabipla/shared";
import { useProfileCore, profileKeys as sharedProfileKeys } from "@tabipla/hooks/profile";
import { userAtom } from "../atoms/auth-atoms";
import { apiClient } from "@/infrastructure/api/client";
import { profileKeys, groupKeys } from "./queryKeys";

// Re-export query keys for direct access if needed
export { sharedProfileKeys as profileKeys };

interface PaymentInfoDto {
  payPayUrl: string | null;
}

export function useProfile() {
  const [user, setUser] = useAtom(userAtom);
  const queryClient = useQueryClient();

  // Waritto固有: アバター更新時にグループ一覧も無効化
  const core = useProfileCore({
    apiClient,
    getUserId: () => user?.id ?? null,
    getUser: () => user,
    setUser,
    additionalInvalidateKeys: [[...groupKeys.all]],
  });

  // Waritto固有: 支払い情報取得
  const paymentInfo = useQuery({
    queryKey: profileKeys.paymentInfo,
    queryFn: async () => {
      const response = await apiClient.get<PaymentInfoDto>("/api/users/payment-info");
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch payment info");
      }
      return response.data!;
    },
    enabled: !!user,
  });

  // Waritto固有: 支払い情報更新
  const updatePaymentInfo = useMutation({
    mutationFn: async (payPayUrl: string | null) => {
      const response = await apiClient.put<PaymentInfoDto>("/api/users/payment-info", {
        payPayUrl,
      });
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to update payment info");
      }
      return response.data!;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: profileKeys.paymentInfo });
    },
  });

  return {
    user,
    paymentInfo,
    updatePaymentInfo,
    ...core,
  };
}
